import React, { useState, useEffect, useCallback, useRef } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  ArrowLeft,
  Plus,
  Minus,
  Printer,
  Check,
  X,
  Eye,
  EyeClosed,
  Search,
  ChevronDown,
  Loader2,
  Download,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useAppSelector } from "../../store/hooks";
import { hasFeatureAccess, FEATURES, getUserRole, USER_ROLES } from "../../config/roleFeatures";
import PublishSaleModal from "./components/PublishSaleModal";
import CancelSaleModal from "./components/CancelSaleModal";
import AddProductModal from "./components/AddProduct";
import StatusBadge from "./components/StatusBadge";
import DeleteIcon from "../../assets/delete-modal-icon.svg";
import { useViewSalesData } from "./hooks/useViewSalesData";
import { customersAPI, salesAPI } from "../../services";
import { useDebounce } from "../../hooks/useDebounce";

import SaveIcon from "../../assets/icons/SaveIcon";
import NameEditIcon from "../../assets/icons/NameEditIcon";
import RedDeleteIcon from "../../assets/icons/RedDeleteIcon";
import { useSelector } from "react-redux";

const ViewSales = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAppSelector((state) => state.auth);
  const userRole = getUserRole(user);
  const canViewProfits = hasFeatureAccess(userRole, FEATURES.SALES_PROFIT);
  const canViewPreviousBalance = hasFeatureAccess(
    userRole,
    FEATURES.PREVIOUS_BALANCE
  );
  const canViewSummary = hasFeatureAccess(userRole, FEATURES.SALES_SUMMARY);
  const isAdmin = userRole === USER_ROLES.ADMIN;
  const { isPartsURL, isAccessoriesURL } = useSelector((state) => state.domain);
  // Use the custom hook for API integration
  const {
    sale,
    products,
    loading,
    error,
    actionLoading,
    updateProductQuantity,
    updateProductSellPrice,
    addProduct,
    deleteProduct,
    refreshData,
    setSale,
    setProducts,
  } = useViewSalesData(id);

  // Local state for editable fields - initialize from API data
  const [discount, setDiscount] = useState(sale?.flatDiscount || 0);
  const [shippingFee, setShippingFee] = useState(sale?.shippingAmount || 0);
  const [paidAmount, setPaidAmount] = useState(sale?.paidAmount || 0);
  const [quantityInputs, setQuantityInputs] = useState({});

  // Debounced values for API calls - reduced to 1 second for better responsiveness
  const debouncedDiscount = useDebounce(discount, 1000);
  const debouncedShippingFee = useDebounce(shippingFee, 1000);
  const debouncedPaidAmount = useDebounce(paidAmount, 1000);

  // Debounced products for auto-save - reduced to 1 second for better responsiveness
  const debouncedProducts = useDebounce(products, 1000);

  // Track if any fields have been modified
  const [hasModifications, setHasModifications] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [lastAutoSaveTime, setLastAutoSaveTime] = useState(0);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const [lastInputTime, setLastInputTime] = useState(0);

  // Snapshot-based queue system for auto-save
  const isApiCallActiveRef = useRef(false); // Flag: true when API call is in progress
  const snapshotSentRef = useRef(null); // Snapshot of data that was sent to API
  const retryTimeoutRef = useRef(null); // Timeout for batching retries

  // Store latest user values (real-time updates)
  const latestDiscountRef = useRef(0);
  const latestShippingFeeRef = useRef(0);
  const latestPaidAmountRef = useRef(0);
  const latestProductsRef = useRef([]);
  const latestCustomerRef = useRef(null);

  // Customer dropdown states (moved before helper functions that use them)
  const [isCustomerDropdownOpen, setIsCustomerDropdownOpen] = useState(false);
  const [customerSearchTerm, setCustomerSearchTerm] = useState("");
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerLoading, setCustomerLoading] = useState(false);

  // Helper function to create a snapshot of current data
  const createDataSnapshot = useCallback(() => {
    const productsToUse =
      latestProductsRef.current.length > 0
        ? latestProductsRef.current
        : products;

    return {
      discount: latestDiscountRef.current,
      shippingFee: latestShippingFeeRef.current,
      paidAmount: latestPaidAmountRef.current,
      products: productsToUse,
      customer: latestCustomerRef.current || selectedCustomer,
    };
  }, [products, selectedCustomer]);

  // Helper function to check if two snapshots are different
  const areSnapshotsDifferent = useCallback((snapshot1, snapshot2) => {
    if (!snapshot1 || !snapshot2) return true;

    // Compare simple values
    if (Math.abs(snapshot1.discount - snapshot2.discount) > 0.01) return true;
    if (Math.abs(snapshot1.shippingFee - snapshot2.shippingFee) > 0.01)
      return true;
    if (Math.abs(snapshot1.paidAmount - snapshot2.paidAmount) > 0.01)
      return true;
    if (snapshot1.customer?.id !== snapshot2.customer?.id) return true;

    // Compare products
    const products1 = snapshot1.products || [];
    const products2 = snapshot2.products || [];

    if (products1.length !== products2.length) return true;

    // Create a map for quick comparison
    const productsMap1 = new Map(
      products1.map((p) => [
        p.productId || p.id,
        {
          qty: p.quantity,
          price: Number(p.sellPrice).toFixed(2),
          unique_id: p.unique_id,
        },
      ])
    );

    for (const product of products2) {
      const key = product.productId || product.id;
      const p1 = productsMap1.get(key);
      if (!p1) return true;
      if (p1.qty !== product.quantity) return true;
      if (p1.price !== Number(product.sellPrice).toFixed(2)) return true;
      if (p1.unique_id !== product.unique_id) return true;
    }

    return false;
  }, []);

  // TanStack Query mutation for auto-save
  const autoSaveMutation = useMutation({
    mutationFn: async (cartData) => {
      isApiCallActiveRef.current = true;
      return await salesAPI.updateCart(id, cartData);
    },
    onSuccess: (response) => {
      const serverData = response?.data?.data || response?.data || response;
      if (serverData) {
        const serverDiscount = parseFloat(serverData.flat_discount) || 0;
        const serverShippingFee = parseFloat(serverData.shipping_amount) || 0;
        const serverPaidAmount = parseFloat(serverData.paid_amount) || 0;

        // Check if there were changes during API call before updating state
        const currentSnapshot = createDataSnapshot();
        const hasChanges = areSnapshotsDifferent(
          snapshotSentRef.current,
          currentSnapshot
        );

        // Only update state if no changes were made during the API call
        if (!hasChanges) {
          setDiscount(serverDiscount);
          setShippingFee(serverShippingFee);
          setPaidAmount(serverPaidAmount);
        }

        initialDiscountRef.current = serverDiscount;
        initialShippingFeeRef.current = serverShippingFee;
        initialPaidAmountRef.current = serverPaidAmount;

        setSale((prev) =>
          prev
            ? {
              ...prev,
              id: serverData.id || prev.id,
              invoiceId: serverData.invoice_id || prev.invoiceId,
              customerName: serverData.customer_name || prev.customerName,
              creator_name: serverData.creator_name || prev.creator_name,
              amount: parseFloat(serverData.total_amount) || 0,
              subTotal: parseFloat(serverData.sub_total) || 0,
              netProfit: parseFloat(serverData.net_profit) || 0,
              resultingBalance: parseFloat(serverData.resulting_balance) || 0,
              totalQty: serverData.total_qty || 0,
              customer: serverData.customer || prev.customer,
              // Only update when API sends an explicit boolean; otherwise keep old value
              canPublish:
                typeof serverData.can_publish === "boolean"
                  ? serverData.can_publish
                  : prev.canPublish,
              createdAt: serverData.created_at || prev.createdAt,
              updatedAt: serverData.updated_at || prev.updatedAt,
              flatDiscount: serverDiscount,
              shippingAmount: serverShippingFee,
              paidAmount: serverPaidAmount,
            }
            : null
        );

        // Only update products if no changes were made during the API call
        if (serverData.cart_items && !hasChanges) {
          const transformedProducts = serverData.cart_items
            .slice()
            .reverse()
            .map((item) => ({
              id: item.id,
              productId: item.product,
              name: item.product_detail?.name || "Unknown Product",
              brand: item.product_detail?.brand_name || "",
              model: item.product_detail?.model_name || "",
              category: item.product_detail?.category_name || "Mobile",
              quantity: item.qty || 0,
              sellPrice: parseFloat(item.price) || 0,
              discounted_sub_total: parseFloat(item.discounted_sub_total) || 0,
              buyPrice: parseFloat(item.buying_price) || 0,
              flatDiscount: parseFloat(item.flat_discount) || 0,
              effectivePrice: parseFloat(item.effective_price) || 0,
              subTotal: parseFloat(item.sub_total) || 0,
              image: item.product_detail?.image || null,
              unique_id:
                item.unique_id || item.product_detail?.unique_id || null,
              in_stock: item.product_detail?.in_stock || 0,
              product_detail: item.product_detail,
              ram: item.product_detail?.ram || "",
              storage: item.product_detail?.storage || "",
              variants: item.product_detail?.variants || [],
              suffix: item.product_detail?.suffix || "",
              used_mobile: item.product_detail?.used_mobile,
            }));

          setProducts(transformedProducts);
        }
      }
      setHasModifications(false);

      // API call completed, release the flag
      isApiCallActiveRef.current = false;

      // Clear any pending retry timeout
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }

      // Check if there were changes during the API call
      const currentSnapshot = createDataSnapshot();
      const hasChanges = areSnapshotsDifferent(
        snapshotSentRef.current,
        currentSnapshot
      );

      if (hasChanges) {
        // Changes detected during API call - batch them with a small delay
        retryTimeoutRef.current = setTimeout(() => {
          retryTimeoutRef.current = null;
          autoSaveChanges();
        }, 150);
      }
    },
    onError: (error) => {
      // API call failed, release the flag
      isApiCallActiveRef.current = false;
      snapshotSentRef.current = null;

      // Clear any pending retry timeout
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }

      let errorMessage = "Failed to save changes.";
      if (error.response?.data?.errors?.items) {
        errorMessage = error.response.data.errors.items;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }
      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 4000,
      });
      refreshData();
    },
  });

  // Auto-save function with snapshot-based queue system
  const autoSaveChanges = useCallback(async () => {
    // If API call is already active, don't start another one
    if (isApiCallActiveRef.current) {
      return;
    }

    if (!sale || !hasUserInteracted) return;

    // Create snapshot of current data
    const currentSnapshot = createDataSnapshot();

    // Prepare items array
    const items =
      currentSnapshot.products?.map((product) => ({
        product: product.productId || product.id,
        qty: product.quantity,
        sell_price: product.sellPrice,
      })) || [];

    const calcPreviousBalance = sale?.previousBalance || 0;

    const cartData = {
      customer: currentSnapshot.customer?.id || sale?.customer || null,
      shipping_amount: currentSnapshot.shippingFee.toString(),
      send_by_post: false,
      previous_balance: calcPreviousBalance.toString(),
      paid_amount: currentSnapshot.paidAmount.toString(),
      flat_discount: currentSnapshot.discount.toString(),
      status: "DRAFT",
      items: items,
    };

    // Save the snapshot that we're about to send
    snapshotSentRef.current = currentSnapshot;

    // Make the API call
    autoSaveMutation.mutate(cartData);
  }, [sale, hasUserInteracted, createDataSnapshot, autoSaveMutation]);

  // Modal states
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showAddProductModal, setShowAddProductModal] = useState(false);
  const [deletingProduct, setDeletingProduct] = useState(null);
  const [showDeleteProductModal, setShowDeleteProductModal] = useState(false);
  const [showLastProductErrorModal, setShowLastProductErrorModal] =
    useState(false);
  const [showProfits, setShowProfits] = useState(false);
  const [hoveredBuyPrice, setHoveredBuyPrice] = useState(null);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedCustomerName, setEditedCustomerName] = useState(
    sale?.customerName || ""
  );
  const [imageLoadingStates, setImageLoadingStates] = useState({});

  // Update local state when sale data changes
  useEffect(() => {
    if (sale) {
      setDiscount(sale.flatDiscount || 0);
      setShippingFee(sale.shippingAmount || 0);
      setPaidAmount(sale.paidAmount || 0);
      setEditedCustomerName(sale.customerName || "");

      // Initialize latest refs with sale data
      latestDiscountRef.current = sale.flatDiscount || 0;
      latestShippingFeeRef.current = sale.shippingAmount || 0;
      latestPaidAmountRef.current = sale.paidAmount || 0;

      // Reset modifications when sale data is loaded
      setHasModifications(false);
      // Mark as initialized after a shorter delay to enable auto-save
      setTimeout(() => {
        setIsInitialized(true);
      }, 1000);
    }
  }, [sale]);

  // Helper function to format number to 2 decimal places
  const formatToTwoDecimals = (value) => {
    const num = parseFloat(value);
    if (isNaN(num)) return 0;
    return Math.round(num * 100) / 100; // Round to 2 decimal places
  };

  // Track modifications for discount
  const handleDiscountChange = (value) => {
    // Disable editing if status is not draft
    if (sale?.status?.toLowerCase() !== "draft") {
      return;
    }
    setHasUserInteracted(true);
    setLastInputTime(Date.now());
    const numValue = parseFloat(value);
    // Prevent negative values
    if (numValue < 0) {
      const newValue = 0;
      latestDiscountRef.current = newValue;
      setDiscount(newValue);
      return;
    }
    const formattedValue = formatToTwoDecimals(value);

    // Always update ref first, then state
    latestDiscountRef.current = formattedValue;
    setDiscount(formattedValue);
    setHasModifications(true);
  };

  // Track modifications for shipping fee
  const handleShippingFeeChange = (value) => {
    // Disable editing if status is not draft
    if (sale?.status?.toLowerCase() !== "draft") {
      return;
    }
    setHasUserInteracted(true);
    setLastInputTime(Date.now());
    const numValue = parseFloat(value);
    // Prevent negative values
    if (numValue < 0) {
      const newValue = 0;
      latestShippingFeeRef.current = newValue;
      setShippingFee(newValue);
      return;
    }
    const formattedValue = formatToTwoDecimals(value);

    latestShippingFeeRef.current = formattedValue;
    setShippingFee(formattedValue);
    setHasModifications(true);
  };

  // Track modifications for paid amount
  const handlePaidAmountChange = (value) => {
    // Disable editing if status is not draft
    if (sale?.status?.toLowerCase() !== "draft") {
      return;
    }
    setHasUserInteracted(true);
    setLastInputTime(Date.now());
    const numValue = parseFloat(value);
    // Prevent negative values
    if (numValue < 0) {
      const newValue = 0;
      latestPaidAmountRef.current = newValue;
      setPaidAmount(newValue);
      return;
    }
    const formattedValue = formatToTwoDecimals(value);

    latestPaidAmountRef.current = formattedValue;
    setPaidAmount(formattedValue);
    setHasModifications(true);
  };

  // Use refs to store initial values to avoid triggering re-renders
  const initialDiscountRef = useRef(0);
  const initialShippingFeeRef = useRef(0);
  const initialPaidAmountRef = useRef(0);
  const initialCustomerIdRef = useRef(null);

  // Update initial values when sale data loads
  useEffect(() => {
    if (sale && isInitialized) {
      initialDiscountRef.current = sale.flatDiscount || 0;
      initialShippingFeeRef.current = sale.shippingAmount || 0;
      initialPaidAmountRef.current = sale.paidAmount || 0;
      initialCustomerIdRef.current = sale.customer || null;
    }
  }, [sale, isInitialized]); // Update when sale changes

  // Keep latestProductsRef in sync with products state
  useEffect(() => {
    latestProductsRef.current = products;
  }, [products]);

  useEffect(() => {
    setQuantityInputs((prev) => {
      const next = {};
      products.forEach((product) => {
        const key = product.id;
        const qtyValue =
          product.quantity !== undefined && product.quantity !== null
            ? String(product.quantity)
            : "0";
        const prevValue = prev[key];
        const shouldSync =
          prevValue === undefined || Number(prevValue) !== Number(product.quantity);
        next[key] = shouldSync ? qtyValue : prevValue;
      });
      return next;
    });
  }, [products]);

  // Keep latest refs in sync with state changes
  useEffect(() => {
    latestDiscountRef.current = discount;
  }, [discount]);

  useEffect(() => {
    latestShippingFeeRef.current = shippingFee;
  }, [shippingFee]);

  useEffect(() => {
    latestPaidAmountRef.current = paidAmount;
  }, [paidAmount]);

  useEffect(() => {
    if (selectedCustomer !== null) {
      latestCustomerRef.current = selectedCustomer;
    }
  }, [selectedCustomer]);

  // Auto-save effect for debounced values and customer changes
  useEffect(() => {
    if (!sale || !isInitialized || !hasUserInteracted) return;

    // Compare against initial values to detect actual changes
    const hasDiscountChange =
      Math.abs(debouncedDiscount - initialDiscountRef.current) > 0.01;
    const hasShippingChange =
      Math.abs(debouncedShippingFee - initialShippingFeeRef.current) > 0.01;
    const hasPaidAmountChange =
      Math.abs(debouncedPaidAmount - initialPaidAmountRef.current) > 0.01;

    // Customer change: only if selectedCustomer is set AND different from initial
    // If selectedCustomer is null, use the sale's customer (no change)
    const currentCustomerId = selectedCustomer?.id || sale?.customer || null;
    const hasCustomerChange =
      selectedCustomer !== null &&
      currentCustomerId !== initialCustomerIdRef.current;

    // If no changes, skip auto-save
    if (
      !hasDiscountChange &&
      !hasShippingChange &&
      !hasPaidAmountChange &&
      !hasCustomerChange
    ) {
      return;
    }

    // If API call is already active, update refs to prevent re-triggering and return
    if (isApiCallActiveRef.current) {
      // Update refs immediately so this effect won't trigger again with the same values
      initialDiscountRef.current = debouncedDiscount;
      initialShippingFeeRef.current = debouncedShippingFee;
      initialPaidAmountRef.current = debouncedPaidAmount;
      initialCustomerIdRef.current = currentCustomerId;
      return;
    }

    // Prevent auto-save if it was called recently (within 3 seconds)
    const now = Date.now();
    if (now - lastAutoSaveTime < 3000) {
      return;
    }

    // Prevent auto-save if user is still actively typing (within 1 second of last input)
    const timeSinceLastInput = now - lastInputTime;
    if (timeSinceLastInput < 1000) {
      return;
    }

    // All checks passed, trigger auto-save

    setLastAutoSaveTime(now);
    // Update initial values after triggering save
    initialDiscountRef.current = debouncedDiscount;
    initialShippingFeeRef.current = debouncedShippingFee;
    initialPaidAmountRef.current = debouncedPaidAmount;
    initialCustomerIdRef.current = currentCustomerId;
    autoSaveChanges();
  }, [
    debouncedDiscount,
    debouncedShippingFee,
    debouncedPaidAmount,
    selectedCustomer,
    autoSaveMutation.isPending,
    isInitialized,
    hasUserInteracted,
    sale,
    lastAutoSaveTime,
    lastInputTime,
    autoSaveChanges,
  ]);

  // Use ref to store serialized products for comparison
  const lastSavedProductsRef = useRef("");
  const isInitialProductLoadRef = useRef(true);

  // Auto-save effect for product changes
  useEffect(() => {
    if (!sale || !debouncedProducts || !isInitialized || !hasUserInteracted)
      return;

    // Serialize products for comparison (only the fields we send to API)
    // Sort by ID to ensure consistent comparison
    const currentProductsKey = JSON.stringify(
      debouncedProducts
        .map((p) => ({
          id: p.productId || p.id,
          qty: p.quantity,
          price: Number(p.sellPrice).toFixed(2), // Normalize to 2 decimals
          unique_id: p.unique_id || null,
        }))
        .sort((a, b) => a.id - b.id) // Sort to prevent false positives from reordering
    );

    // Initialize on first load
    if (isInitialProductLoadRef.current) {
      lastSavedProductsRef.current = currentProductsKey;
      isInitialProductLoadRef.current = false;
      return;
    }

    // Check if products have actually changed from last save
    if (currentProductsKey === lastSavedProductsRef.current) {
      // No changes, skip auto-save
      return;
    }

    // If API call is already active, update ref to prevent re-triggering and return
    if (isApiCallActiveRef.current) {
      // Update ref immediately so this effect won't trigger again with the same values
      lastSavedProductsRef.current = currentProductsKey;
      return;
    }

    // Prevent auto-save if it was called recently (within 3 seconds)
    const now = Date.now();
    if (now - lastAutoSaveTime < 3000) {
      return;
    }

    // Prevent auto-save if user is still actively typing (within 1 second of last input)
    const timeSinceLastInput = now - lastInputTime;
    if (timeSinceLastInput < 1000) {
      return;
    }

    // Products have changed, trigger auto-save

    setLastAutoSaveTime(now);
    lastSavedProductsRef.current = currentProductsKey;
    autoSaveChanges();
  }, [
    debouncedProducts,
    autoSaveMutation.isPending,
    isInitialized,
    hasUserInteracted,
    sale,
    lastAutoSaveTime,
    lastInputTime,
    autoSaveChanges,
  ]);

  // Calculate totals from API data or local calculations
  const totalQty =
    products?.reduce((sum, product) => sum + (product.quantity || 0), 0) || 0;
  const totalBuyPrice =
    products?.reduce(
      (sum, product) => sum + (product.buyPrice || 0) * (product.quantity || 0),
      0
    ) || 0;
  const totalSellPrice =
    products?.reduce(
      (sum, product) =>
        sum + (parseFloat(product.sellPrice) || 0) * (product.quantity || 0),
      0
    ) || 0;

  const productTotal =
    products?.reduce(
      (sum, product) =>
        sum +
        (parseFloat(product.discounted_sub_total) ||
          (parseFloat(product.sellPrice) || 0) * (product.quantity || 0)),
      0
    ) || 0;

  // Total amount = product total + shipping - discount
  const totalAmount =
    productTotal + (parseFloat(shippingFee) || 0) - (parseFloat(discount) || 0);
  const previousBalance = sale?.previousBalance || 0;
  // Sub-total = total amount - previous balance (amount due before this payment)
  const subTotal = totalAmount - previousBalance;
  const resultingBalance = paidAmount - subTotal;
  const netProfit = sale?.netProfit || totalSellPrice - totalBuyPrice;

  const updateQuantity = (productId, newQuantity) => {
    // Disable editing if status is not draft
    if (sale?.status?.toLowerCase() !== "draft") {
      return;
    }
    setHasUserInteracted(true);
    setLastInputTime(Date.now());

    // Prevent negative quantities
    if (newQuantity < 0) {
      return;
    }

    const product = products.find((p) => p.id === productId);

    // Clamp to available stock (current qty + remaining in_stock)
    if (product) {
      const hasStockLimit =
        typeof product.in_stock === "number" && product.in_stock >= 0;
      const maxAllowed = hasStockLimit
        ? Math.max(0, (product.quantity || 0) + product.in_stock)
        : newQuantity;

      if (hasStockLimit && newQuantity > maxAllowed) {
        toast.warning(`Only ${maxAllowed} in stock`, {
          position: "top-right",
          autoClose: 2000,
        });
        // Don't allow exceeding the max
        return;
      }
    }

    if (product && newQuantity === product.quantity) {
      return;
    }

    const updatedProducts = latestProductsRef.current.map((p) =>
      p.id === productId ? { ...p, quantity: newQuantity } : p
    );
    latestProductsRef.current = updatedProducts;

    updateProductQuantity(productId, newQuantity);
    setHasModifications(true);
  };

  const handleQuantityInputChange = (product, value) => {
    if (sale?.status?.toLowerCase() !== "draft") {
      return;
    }

    if (!/^\d*$/.test(value)) {
      return;
    }

    const key = product.id;

    // Check stock limit before allowing the input
    if (value !== "") {
      const parsed = Number.parseInt(value, 10);
      if (!Number.isNaN(parsed)) {
        const hasStockLimit =
          typeof product.in_stock === "number" && product.in_stock >= 0;
        const currentQty = product.quantity || 0;
        const maxAllowed = hasStockLimit ? currentQty + product.in_stock : parsed;

        // Prevent input if it exceeds stock limit
        if (hasStockLimit && parsed > maxAllowed) {
          toast.warning(`Only ${maxAllowed} in stock`, {
            position: "top-right",
            autoClose: 2000,
          });
          // Don't update the input, keep the previous value
          return;
        }

        // Update both input state and quantity
        setQuantityInputs((prev) => ({ ...prev, [key]: value }));
        updateQuantity(product.id, parsed);
        return;
      }
    }

    // Allow empty string
    setQuantityInputs((prev) => ({ ...prev, [key]: value }));
  };

  const handleQuantityInputBlur = (product) => {
    const key = product.id;
    setQuantityInputs((prev) => {
      const current = prev[key];
      if (current !== "" && current !== undefined) {
        return prev;
      }

      const fallback =
        product.quantity !== undefined && product.quantity !== null
          ? String(product.quantity)
          : "0";
      return { ...prev, [key]: fallback };
    });
  };

  const updateSellPrice = (productId, newPrice) => {
    // Disable editing if status is not draft
    if (sale?.status?.toLowerCase() !== "draft") {
      return;
    }
    setHasUserInteracted(true);
    setLastInputTime(Date.now());

    // Handle empty string - allow it to be empty
    if (newPrice === "" || newPrice === null || newPrice === undefined) {
      updateProductSellPrice(productId, "");
      latestProductsRef.current = products.map((p) =>
        p.id === productId ? { ...p, sellPrice: "" } : p
      );
      setHasModifications(true);
      return;
    }

    const numPrice = parseFloat(newPrice);
    // Prevent negative prices
    if (numPrice < 0) {
      return;
    }

    // If it's a valid number, format it
    if (!isNaN(numPrice)) {
      const formattedPrice = formatToTwoDecimals(newPrice);
      updateProductSellPrice(productId, formattedPrice);
      latestProductsRef.current = products.map((p) =>
        p.id === productId ? { ...p, sellPrice: formattedPrice } : p
      );
    } else {
      // If it's not a valid number, just set the raw value
      updateProductSellPrice(productId, newPrice);
      latestProductsRef.current = products.map((p) =>
        p.id === productId ? { ...p, sellPrice: newPrice } : p
      );
    }
    setHasModifications(true);
  };

  const handleDeleteProduct = (productId) => {
    // Disable editing if status is not draft
    if (sale?.status?.toLowerCase() !== "draft") {
      return;
    }

    // Check if this is the last product
    if (products.length <= 1) {
      setShowDeleteProductModal(false);
      setDeletingProduct(null);
      setShowLastProductErrorModal(true);
      return;
    }

    setHasUserInteracted(true);
    setLastInputTime(Date.now());

    const updatedProducts = latestProductsRef.current.filter((p) => p.id !== productId);
    latestProductsRef.current = updatedProducts;

    deleteProduct(productId);
    setShowDeleteProductModal(false);
    setDeletingProduct(null);
    setHasModifications(true);
  };

  const handleEditName = () => {
    // Disable editing if status is not draft
    if (sale?.status?.toLowerCase() !== "draft") {
      return;
    }
    setIsEditingName(true);
  };

  const handleSaveName = async () => {
    if (!selectedCustomer) return;

    setEditedCustomerName(selectedCustomer.name);
    setIsEditingName(false);
    setHasUserInteracted(true);
    setLastInputTime(Date.now());
    latestCustomerRef.current = selectedCustomer;
    setHasModifications(true);
  };

  const handleCancelEdit = () => {
    setEditedCustomerName(sale?.customerName || "");
    setSelectedCustomer(null);
    setIsEditingName(false);
  };

  // Fetch customers when dropdown opens
  const fetchCustomers = useCallback(async () => {
    setCustomerLoading(true);
    try {
      const { response, error } = await customersAPI.getCustomers(
        customerSearchTerm,
        1,
        50
      );
      if (error) {
        console.error("Error fetching customers:", error);
        setCustomers([]);
      } else {
        let customersList = [];
        if (response) {
          if (Array.isArray(response)) {
            customersList = response;
          } else if (response.results && Array.isArray(response.results)) {
            customersList = response.results;
          } else if (response.data && Array.isArray(response.data)) {
            customersList = response.data;
          }
        }
        setCustomers(customersList);
      }
    } catch (err) {
      console.error("Error fetching customers:", err);
      setCustomers([]);
    } finally {
      setCustomerLoading(false);
    }
  }, [customerSearchTerm]);

  const handleCustomerSelect = (customer) => {
    setSelectedCustomer(customer);
    setEditedCustomerName(customer.name);
    setIsCustomerDropdownOpen(false);
    setCustomerSearchTerm("");
    setHasModifications(true);
  };

  // Wrapper function to track product additions
  const handleAddProduct = async (product) => {
    // Disable editing if status is not draft
    if (sale?.status?.toLowerCase() !== "draft") {
      return;
    }

    // Check if product already exists
    const existingProduct = products.find((p) => {
      // For products with unique_id (barcode scanned), match by unique_id only
      if (product.unique_id) {
        return p.unique_id === product.unique_id;
      }
      // For regular products, match by product ID only if both don't have unique_id
      if (!p.unique_id) {
        return p.productId === product.id || p.id === product.id;
      }
      return false;
    });

    if (existingProduct) {
      // Increase quantity of existing product
      updateQuantity(
        existingProduct.id,
        existingProduct.quantity + (product.quantity || 1)
      );
      toast.info(`Increased quantity of "${existingProduct.name}"`, {
        position: "top-right",
        autoClose: 2000,
      });
    } else {
      // Add new product
      addProduct(product);
      // Update latest products ref immediately for new product
      latestProductsRef.current = [product, ...products];
    }

    setHasModifications(true);
    setHasUserInteracted(true);
    setLastInputTime(Date.now());
  };

  // Handle save changes
  const handleSaveChanges = async () => {
    if (!hasModifications) {
      toast.info("No changes to save.", {
        position: "top-right",
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      return;
    }

    setSaveLoading(true);
    try {
      // Format products into items array according to API schema
      const items =
        products?.map((product) => {
          const itemData = {
            product: product.productId || product.id, // Use productId if available, fallback to id
            qty: product.quantity,
            sell_price: product.sellPrice,
            discounted_sub_total: product.discounted_sub_total || 0,
          };

          return itemData;
        }) || [];

      const cartData = {
        customer: selectedCustomer?.id || sale?.customer || null,
        shipping_amount: shippingFee.toString(),
        send_by_post: false,
        previous_balance: previousBalance.toString(),
        paid_amount: paidAmount.toString(),
        flat_discount: discount.toString(),
        status: "DRAFT",
        items: items,
      };

      const response = await salesAPI.updateCart(id, cartData);

      // Update local state with server response to show real-time updates
      // Handle both wrapped (response.data.data) and unwrapped (response.data) structures
      const serverData = response?.data?.data || response?.data || response;
      if (serverData) {
        // Update local state variables from server response to reflect saved values
        const serverDiscount = parseFloat(serverData.flat_discount) || 0;
        const serverShippingFee = parseFloat(serverData.shipping_amount) || 0;
        const serverPaidAmount = parseFloat(serverData.paid_amount) || 0;

        // Update local state variables (this won't trigger auto-save because we update refs first)
        setDiscount(serverDiscount);
        setShippingFee(serverShippingFee);
        setPaidAmount(serverPaidAmount);

        // Update initial refs to prevent triggering another auto-save
        initialDiscountRef.current = serverDiscount;
        initialShippingFeeRef.current = serverShippingFee;
        initialPaidAmountRef.current = serverPaidAmount;

        // Update sale data from server response
        setSale((prev) =>
          prev
            ? {
              ...prev,
              id: serverData.id || prev.id,
              invoiceId: serverData.invoice_id || prev.invoiceId,
              customerName: serverData.customer_name || prev.customerName,
              creator_name: serverData.creator_name || prev.creator_name,
              amount: parseFloat(serverData.total_amount) || 0,
              subTotal: parseFloat(serverData.sub_total) || 0,
              netProfit: parseFloat(serverData.net_profit) || 0,
              resultingBalance: parseFloat(serverData.resulting_balance) || 0,
              totalQty: serverData.total_qty || 0,
              customer: serverData.customer || prev.customer,
              // Only update when API sends an explicit boolean; otherwise keep old value
              canPublish:
                typeof serverData.can_publish === "boolean"
                  ? serverData.can_publish
                  : prev.canPublish,
              createdAt: serverData.created_at || prev.createdAt,
              updatedAt: serverData.updated_at || prev.updatedAt,
              // Update these from server response to keep in sync
              flatDiscount: serverDiscount,
              shippingAmount: serverShippingFee,
              paidAmount: serverPaidAmount,
            }
            : null
        );

        // Update products with server response - use server as source of truth
        if (serverData.cart_items) {
          // Transform cart items to products - reverse order to show newest first
          const transformedProducts = serverData.cart_items
            .slice()
            .reverse()
            .map((item) => ({
              id: item.id,
              productId: item.product,
              name: item.product_detail?.name || "Unknown Product",
              brand: item.product_detail?.brand_name || "",
              model: item.product_detail?.model_name || "",
              category: item.product_detail?.category_name || "Mobile",
              quantity: item.qty || 0,
              sellPrice: parseFloat(item.price) || 0,
              discounted_sub_total: parseFloat(item.discounted_sub_total) || 0,
              buyPrice: parseFloat(item.buying_price) || 0,
              flatDiscount: parseFloat(item.flat_discount) || 0,
              effectivePrice: parseFloat(item.effective_price) || 0,
              subTotal: parseFloat(item.sub_total) || 0,
              image: item.product_detail?.image || null,
              unique_id:
                item.unique_id || item.product_detail?.unique_id || null,
              in_stock: item.product_detail?.in_stock || 0,
              product_detail: item.product_detail,
              // Add product details for ProductTable format
              ram: item.product_detail?.ram || "",
              storage: item.product_detail?.storage || "",
              variants: item.product_detail?.variants || [],
              suffix: item.product_detail?.suffix || "",
              used_mobile: item.product_detail?.used_mobile,
            }));

          setProducts(transformedProducts);
        }
      }

      setHasModifications(false);
      toast.success("Changes saved successfully!", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } catch (error) {
      console.error("Error saving changes:", error);

      // Extract and show backend validation errors
      let errorMessage = "Failed to save changes. Please try again.";

      if (error.response?.data?.errors?.items) {
        // Handle items validation error (e.g., stock issues)
        errorMessage = error.response.data.errors.items;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }

      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 4000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });

      // Reload data to reset to last valid state
      await refreshData();
    } finally {
      setSaveLoading(false);
    }
  };

  // Handle Publish button click
  const handlePublishClick = () => {
    if (sale?.canPublish === false) {
      toast.error("Cannot publish: customer credit limit exceeded.", {
        position: "top-right",
        autoClose: 4000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      return;
    }
    if (
      actionLoading ||
      sale?.status !== "draft" ||
      hasModifications ||
      autoSaveMutation.isPending
    ) {
      if (autoSaveMutation.isPending) {
        toast.warning("Please wait while changes are being saved.", {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      } else if (hasModifications) {
        toast.warning("Please save your changes before publishing the sale.", {
          position: "top-right",
          autoClose: 4000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      } else if (sale?.status !== "draft") {
        toast.info("This sale cannot be published in its current status.", {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      }
      return;
    }
    setShowPublishModal(true);
  };

  const handleExportSale = async (format) => {
    if (sale?.status?.toLowerCase() !== "publish") {
      toast.warning("Only published sales can be exported for retail stock import.");
      return;
    }
    setExportLoading(true);
    try {
      const filename = await salesAPI.exportSale(id, format);
      toast.success(`Exported ${filename}`);
    } catch (error) {
      console.error("Sale export failed:", error);
      toast.error(error?.message || "Failed to export sale.");
    } finally {
      setExportLoading(false);
    }
  };

  // Handle actual publish
  const handlePublishConfirm = async () => {
    try {
      await salesAPI.publishSale(id);
      toast.success("Sale published successfully!", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      setShowPublishModal(false);
      // Refresh data to get updated status
      refreshData();
    } catch (error) {
      console.error("Error publishing sale:", error);

      // Extract error message from backend response
      let errorMessage = "Failed to publish sale. Please try again.";

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.message) {
        errorMessage = error.message;
      }

      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 4000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    }
  };

  // Handle cancel sale
  const handleCancelConfirm = async (reason) => {
    try {
      await salesAPI.cancelSale(id);
      toast.success("Sale cancelled successfully!", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      setShowCancelModal(false);
      // Refresh data to get updated status
      refreshData();
    } catch (error) {
      console.error("Error cancelling sale:", error);
      toast.error("Failed to cancel sale. Please try again.", {
        position: "top-right",
        autoClose: 4000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    }
  };

  const filteredCustomers = customers.filter((customer) =>
    customer.name.toLowerCase().includes(customerSearchTerm.toLowerCase())
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isCustomerDropdownOpen &&
        !event.target.closest(".customer-dropdown-container")
      ) {
        setIsCustomerDropdownOpen(false);
      }
    };

    if (isCustomerDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isCustomerDropdownOpen]);

  // Fetch customers when dropdown opens or search term changes
  useEffect(() => {
    if (isCustomerDropdownOpen) {
      fetchCustomers();
    }
  }, [isCustomerDropdownOpen, customerSearchTerm, fetchCustomers]);

  const saleData = {
    totalAmount,
    paidAmount,
    resultingBalance,
  };

  // Show loading state
  if (loading) {
    return (
      <div className="figma-sales-view">
        <div className="loading-container">
          <p>Loading sale details...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error || !sale) {
    return (
      <div className="figma-sales-view">
        <div className="error-container">
          <p>Error loading sale details: {error || "Sale not found"}</p>
          <button onClick={refreshData} className="retry-btn">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="figma-sales-view">
      {/* Auto-save Loader Indicator */}
      {autoSaveMutation.isPending && (
        <div className="auto-save-floating-indicator">
          <Loader2 size={16} className="spinning-loader" />
          <span>Auto-saving changes...</span>
        </div>
      )}

      {/* Back Button - Outside main container */}
      <div className="figma-back-container">
        <button
          className={`${isPartsURL
              ? "figma-back-btn-parts"
              : isAccessoriesURL
                ? "figma-back-btn-acc"
                : "figma-back-btn"
            }`}
          onClick={() => navigate("/sales")}
        >
          <ArrowLeft size={15} />
          Back to Sales
        </button>
      </div>

      {/* Customer Info Card - Separate Card */}
      <div className="figma-customer-card">
        <div className="figma-customer-header">
          <div className="figma-customer-info">
            <div className="d-flex align-items-center gap-2">
              {isEditingName ? (
                <div className="d-flex align-items-center gap-2">
                  <div className="customer-dropdown-container">
                    <div
                      className="dropdown-input"
                      onClick={() =>
                        setIsCustomerDropdownOpen(!isCustomerDropdownOpen)
                      }
                    >
                      <span className="selected-value">
                        {selectedCustomer
                          ? selectedCustomer.name
                          : editedCustomerName || "Select customer"}
                      </span>
                      <ChevronDown className="dropdown-arrow" size={16} />
                    </div>

                    {isCustomerDropdownOpen && (
                      <div className="dropdown-menu">
                        <div className="dropdown-search">
                          <Search className="batches-search-icon" size={16} />
                          <input
                            type="text"
                            className={`${isPartsURL
                                ? "batches-search-input-parts"
                                : isAccessoriesURL
                                  ? "batches-search-input-acc"
                                  : "batches-search-input"
                              }`}
                            placeholder="Search customer name..."
                            value={customerSearchTerm}
                            onChange={(e) =>
                              setCustomerSearchTerm(e.target.value)
                            }
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>
                        <div className="dropdown-list noscrollbar">
                          {customerLoading ? (
                            <div className="batches-dropdown-item">
                              Loading customers...
                            </div>
                          ) : filteredCustomers.length > 0 ? (
                            filteredCustomers.map((customer) => (
                              <div
                                key={customer.id}
                                className="batches-dropdown-item"
                                onClick={() => handleCustomerSelect(customer)}
                              >
                                {customer.name}
                              </div>
                            ))
                          ) : (
                            <div className="batches-dropdown-item no-results">
                              No customers found
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  <button
                    className="figma-name-save-btn"
                    onClick={handleSaveName}
                    title="Save"
                  >
                    <Check size={16} />
                  </button>
                  <button
                    className="figma-name-cancel-btn"
                    onClick={handleCancelEdit}
                    title="Cancel"
                  >
                    <X size={16} />
                  </button>
                </div>
              ) : (
                <>
                  <h2 className="figma-customer-name">
                    {editedCustomerName || sale?.customerName}
                  </h2>
                  <button
                    className="figma-name-edit-btn"
                    onClick={handleEditName}
                    title="Edit customer name"
                    disabled={sale?.status?.toLowerCase() !== "draft"}
                  >
                    <NameEditIcon />
                  </button>
                </>
              )}
            </div>
            <p className="figma-customer-date">
              {sale?.createdAt
                ? new Date(sale.createdAt).toLocaleString("en-US", {
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                })
                : "N/A"}
            </p>
          </div>

          <StatusBadge status={sale?.status} />
        </div>

        <div
          className={`figma-customer-details${
            sale?.canPublish === false
              ? " figma-customer-details-cannot-publish"
              : ""
          }`}
        >
          <div className="figma-detail-item">
            <span className="figma-label">Creator</span>
            <span className="figma-value">{sale?.creator_name || "N/A"}</span>
          </div>
          <div className="figma-detail-item">
            <span className="figma-label">Invoice ID</span>
            <span className="figma-value">{sale?.invoiceId}</span>
          </div>
          {canViewPreviousBalance && (
            <div className="figma-detail-item">
              <span className="figma-label">Previous Balance</span>
              <span className="">
                <span
                  className={`balance-badge ${previousBalance >= 0 ? "positive" : "negative"
                    }`}
                >
                  {previousBalance >= 0
                    ? `€${previousBalance.toFixed(2)}`
                    : `€${previousBalance.toFixed(2)}`}
                </span>
              </span>
            </div>
          )}
          <div className="figma-detail-item">
            <span className="figma-label">Total Qty</span>
            <span className="figma-value">{totalQty}</span>
          </div>
          <div className="figma-detail-item">
            <span className="figma-label">Total Amount</span>
            <span className="figma-value">€{totalAmount.toFixed(2)}</span>
          </div>
          {canViewProfits && (
            <div className="figma-detail-item">
              <span className="figma-label">Net Profit</span>
              <div className="d-flex align-items-center gap-2">
                <span
                  className={`sales-detail-profit-stars ${netProfit < 0 ? "negative" : ""
                    }`}
                >
                  {showProfits ? `€${netProfit.toFixed(2)}` : "*******"}
                </span>
                <button
                  className="profit-toggle-btn"
                  onClick={() => setShowProfits(!showProfits)}
                  title={showProfits ? "Hide profit" : "Show profit"}
                >
                  {showProfits ? <EyeClosed size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Two Card Layout */}
      <div
        className={`figma-cards-container ${canViewSummary ? "two-columns" : "single-column"
          }`}
      >
        {/* Products Section - Left Card (Wider) */}
        <div className="figma-products-card">
          <div className="figma-products-header">
            <h3>Products</h3>
            <button
              className={`${isPartsURL
                  ? "figma-add-product-btn-parts"
                  : isAccessoriesURL
                    ? "figma-add-product-btn-acc"
                    : "figma-add-product-btn"
                }`}
              onClick={() => setShowAddProductModal(true)}
              disabled={sale?.status?.toLowerCase() !== "draft"}
            >
              <Plus size={16} />
              Add Product
            </button>
          </div>

          {/* Products List */}
          <div className="figma-products-list">
            {products && products.length > 0 ? (
              products.map((product) => (
                <div key={product.id} className="figma-product-item">
                  {/* Product Image */}
                  <div className="figma-product-image-container">
                    {product.image &&
                      product.image !== "/placeholder-product.png" ? (
                      <>
                        {imageLoadingStates[product.id] && (
                          <div className="image-loading">Loading...</div>
                        )}
                        <img
                          src={product.image}
                          alt={product.name}
                          className="figma-product-image"
                          onLoad={() => {
                            setImageLoadingStates((prev) => ({
                              ...prev,
                              [product.id]: false,
                            }));
                          }}
                          onError={(e) => {
                            setImageLoadingStates((prev) => ({
                              ...prev,
                              [product.id]: false,
                            }));
                            // Hide the image on error and show placeholder
                            e.target.style.display = "none";
                            // Show placeholder instead
                            const placeholder = e.target.nextElementSibling;
                            if (placeholder) {
                              placeholder.style.display = "flex";
                            }
                          }}
                          onLoadStart={() => {
                            setImageLoadingStates((prev) => ({
                              ...prev,
                              [product.id]: true,
                            }));
                          }}
                          style={{
                            display: imageLoadingStates[product.id]
                              ? "none"
                              : "block",
                          }}
                        />
                        <div
                          className="no-image-placeholder"
                          style={{ display: "none" }}
                        >
                          <span>No Image</span>
                        </div>
                      </>
                    ) : (
                      <div className="no-image-placeholder">
                        <span>No Image</span>
                      </div>
                    )}
                  </div>

                  {/* Product Info */}
                  <div className="figma-product-info">
                    <div className="figma-product-name-container">
                      <h4 className="figma-product-name">{product.name}</h4>

                      {/* Product details in same format as ProductTable */}
                      <div
                        style={{
                          fontSize: "0.8em",
                          color: "#666",
                          marginTop: "4px",
                        }}
                      >
                        {product.ram && `${product.ram}`}
                        {product.ram && product.storage && " | "}
                        {product.storage && `${product.storage}`}
                        {product.variants && product.variants.length > 0 && (
                          <>
                            {(product.ram || product.storage) && " | "}
                            {product.variants
                              .map((variant) =>
                                Object.values(variant).join(", ")
                              )
                              .join(", ")}
                          </>
                        )}
                        {product.suffix && (
                          <>
                            {(product.ram ||
                              product.storage ||
                              (product.variants &&
                                product.variants.length > 0)) &&
                              " | "}
                            {product.suffix}
                          </>
                        )}
                        {product.used_mobile !== undefined &&
                          product.used_mobile !== null && (
                            <>
                              {(product.ram ||
                                product.storage ||
                                (product.variants &&
                                  product.variants.length > 0) ||
                                product.suffix) &&
                                " | "}
                              {product.used_mobile === false ? "New" : "Used"}
                            </>
                          )}
                      </div>

                      {/* {product.category && (
                        <p className="figma-product-category">
                          {product.category}
                        </p>
                      )} */}
                    </div>

                    {/* Quantity Control */}
                    <div className="figma-quantity-control">
                      <button
                        className="figma-qty-btn"
                        onClick={() =>
                          updateQuantity(product.id, product.quantity - 1)
                        }
                        disabled={sale?.status?.toLowerCase() !== "draft"}
                      >
                        <Minus size={16} />
                      </button>
                      <input
                        type="number"
                        inputMode="numeric"
                        className="figma-quantity-input"
                        min="0"
                        value={
                          quantityInputs[product.id] !== undefined
                            ? quantityInputs[product.id]
                            : product.quantity || 0
                        }
                        onChange={(e) =>
                          handleQuantityInputChange(product, e.target.value)
                        }
                        onBlur={() => handleQuantityInputBlur(product)}
                        onKeyDown={(e) => {
                          if (e.key === "-" || e.key === "e" || e.key === "E") {
                            e.preventDefault();
                          }
                        }}
                        onWheel={(e) => e.target.blur()}
                        disabled={sale?.status?.toLowerCase() !== "draft"}
                        aria-label="Quantity"
                      />
                      <button
                        className="figma-qty-btn"
                        onClick={() =>
                          updateQuantity(product.id, product.quantity + 1)
                        }
                        disabled={
                          sale?.status?.toLowerCase() !== "draft" ||
                          product.in_stock === 0
                        }
                        style={{
                          cursor:
                            sale?.status?.toLowerCase() !== "draft" ||
                              product.in_stock === 0
                              ? "not-allowed"
                              : "pointer",
                        }}
                        title={
                          product.in_stock === 0
                            ? `No more items available in stock`
                            : product.in_stock > 0
                              ? `${product.in_stock} more items available`
                              : ""
                        }
                      >
                        <Plus size={16} />
                      </button>
                    </div>

                    {/* Product Pricing */}
                    <div className="figma-product-pricing">
                      {canViewProfits && (
                        <div className="figma-buy-price-item">
                          <span className="figma-price-label">Buy Price :</span>
                          <span
                            className="figma-price-value buy-price-hover"
                            onMouseEnter={() => setHoveredBuyPrice(product.id)}
                            onMouseLeave={() => setHoveredBuyPrice(null)}
                          >
                            {hoveredBuyPrice === product.id
                              ? `€${(product.buyPrice || 0).toFixed(2)}`
                              : "******"}
                          </span>
                        </div>
                      )}

                      <div className="figma-sell-price-item">
                        <span className="figma-price-label">Sell Price :</span>

                        <input
                          type="number"
                          className={`${isPartsURL
                              ? "figma-sell-price-input-parts"
                              : isAccessoriesURL
                                ? "figma-sell-price-input-acc"
                                : "figma-sell-price-input"
                            }`}
                          value={product.sellPrice || ""}
                          onChange={(e) => {
                            const value = e.target.value;
                            updateSellPrice(product.id, value);
                          }}
                          onKeyDown={(e) => {
                            // Prevent negative sign and other invalid characters
                            if (
                              e.key === "-" ||
                              e.key === "e" ||
                              e.key === "E"
                            ) {
                              e.preventDefault();
                            }
                          }}
                          onWheel={(e) => e.target.blur()}
                          step="0.01"
                          min="0"
                          style={{ width: "90px" }}
                          placeholder="0.00"
                          disabled={sale?.status?.toLowerCase() !== "draft"}
                        />
                      </div>

                      <div className="figma-sub-total-item">
                        <span className="figma-price-label">Sub-Total:</span>
                        <span className="figma-price-value">
                          €{(product.discounted_sub_total || 0).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>

                  <button
                    className="action-btn delete"
                    title="Delete Sale"
                    onClick={() => {
                      setDeletingProduct(product);
                      setShowDeleteProductModal(true);
                    }}
                    disabled={sale?.status?.toLowerCase() !== "draft"}
                  >
                    <RedDeleteIcon />
                  </button>
                </div>
              ))
            ) : (
              <div className="no-products">
                <p>No products found in this sale.</p>
              </div>
            )}
          </div>
        </div>

        {/* Summary Section - Right Card (Narrower) */}
        {canViewSummary && (
          <div className="figma-summary-card">
            <div className="figma-summary-header">
              <h3>Summary</h3>
              {autoSaveMutation.isPending && (
                <div className="auto-save-indicator" title="Auto-saving...">
                  <Loader2 size={16} className="spinning-loader" />
                  <span>Auto-saving...</span>
                </div>
              )}
            </div>

            <div className="figma-summary-content">
              <div className="figma-summary-row">
                <span className="figma-summary-label">Discount per Qty</span>
                <input
                  type="number"
                  className={`${isPartsURL
                      ? "figma-summary-input-parts"
                      : isAccessoriesURL
                        ? "figma-summary-input-acc"
                        : "figma-summary-input"
                    }`}
                  placeholder="Enter Discount"
                  value={discount || ""}
                  onChange={(e) => handleDiscountChange(e.target.value)}
                  onWheel={(e) => e.target.blur()}
                  step="0.01"
                  min="0"
                  disabled={sale?.status?.toLowerCase() !== "draft"}
                />
              </div>
              <div className="figma-summary-row">
                <span className="figma-summary-label">Shipping Details</span>
                <input
                  type="number"
                  className={`${isPartsURL
                      ? "figma-summary-input-parts"
                      : isAccessoriesURL
                        ? "figma-summary-input-acc"
                        : "figma-summary-input"
                    }`}
                  placeholder="Enter Shipping Fee"
                  value={shippingFee || ""}
                  onChange={(e) => handleShippingFeeChange(e.target.value)}
                  onWheel={(e) => e.target.blur()}
                  step="0.01"
                  min="0"
                  disabled={sale?.status?.toLowerCase() !== "draft"}
                />
              </div>

              <div className="figma-summary-row">
                <span className="figma-summary-label">Total Amount</span>
                <span className="figma-summary-value">
                  €{totalAmount.toFixed(2)}
                </span>
              </div>
              {canViewPreviousBalance && (
                <div className="figma-summary-row">
                  <span className="figma-summary-label">Previous Balance</span>
                  <span className="figma-summary-value">
                    €{previousBalance.toFixed(2)}
                  </span>
                </div>
              )}
              <div className="figma-summary-row">
                <span className="figma-summary-label">Sub-Total</span>
                <span className="figma-summary-value">
                  €{subTotal.toFixed(2)}
                </span>
              </div>
              {isAdmin && (
                <div className="figma-summary-row">
                  <span className="figma-summary-label">Paid Amount</span>
                  <input
                    type="number"
                    className={`${isPartsURL
                        ? "figma-summary-input-parts"
                        : isAccessoriesURL
                          ? "figma-summary-input-acc"
                          : "figma-summary-input"
                      }`}
                    placeholder="Enter Amount"
                    value={paidAmount || ""}
                    onChange={(e) => handlePaidAmountChange(e.target.value)}
                    onWheel={(e) => e.target.blur()}
                    step="0.01"
                    min="0"
                    disabled={sale?.status?.toLowerCase() !== "draft"}
                  />
                </div>
              )}
              <div className="figma-summary-row figma-balance-row">
                <span className="resulting-balance-text">
                  Resulting Balance
                </span>
                <span
                  className={`${isPartsURL
                      ? "resulting-balance-text-parts"
                      : isAccessoriesURL
                        ? "resulting-balance-text-acc"
                        : "resulting-balance-text"
                    } ${resultingBalance >= 0 ? "positive" : "negative"}`}
                >
                  {resultingBalance === 0
                    ? "€0.00"
                    : resultingBalance > 0
                      ? `+€${resultingBalance.toFixed(2)}`
                      : `-€${Math.abs(resultingBalance).toFixed(2)}`}
                </span>
              </div>

              {canViewProfits && (
                <div className="figma-net-profit">
                  <div className="d-flex align-items-center justify-content-center gap-2">
                    <span>
                      Net Profit:{" "}
                      {showProfits ? `€${netProfit.toFixed(2)}` : "*******"}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Action Buttons - Right Side */}
      <div className="figma-actions-container">
        <button
          className="figma-btn figma-print-btn"
          onClick={() => navigate(`/sales/print/${id}`)}
        >
          <Printer size={16} />
          Print
        </button>
        {sale?.status?.toLowerCase() === "publish" && (
          <>
            <button
              className="figma-btn figma-print-btn"
              onClick={() => handleExportSale("xlsx")}
              disabled={exportLoading}
              title="Export Excel for Electronic POS stock import"
            >
              <Download size={16} />
              {exportLoading ? "Exporting..." : "Export Excel"}
            </button>
            <button
              className="figma-btn figma-print-btn"
              onClick={() => handleExportSale("pdf")}
              disabled={exportLoading}
              title="Export PDF for customer"
            >
              <Download size={16} />
              Export PDF
            </button>
          </>
        )}
        {isAdmin && (
          <button
            className={`figma-btn ${isPartsURL
                ? "figma-publish-btn-parts"
                : isAccessoriesURL
                  ? "figma-publish-btn-acc"
                  : "figma-publish-btn"
              }`}
            onClick={handlePublishClick}
            disabled={
              actionLoading ||
              sale?.status !== "draft" ||
              hasModifications ||
              autoSaveMutation.isPending ||
              sale?.canPublish === false
            }
            title={
              sale?.canPublish === false
                ? "Cannot publish: customer credit limit exceeded"
                : hasModifications
                ? "Save changes before publishing"
                : autoSaveMutation.isPending
                  ? "Waiting for changes to save"
                  : ""
            }
          >
            <Check size={16} />
            {actionLoading
              ? "Publishing..."
              : hasModifications
                ? "Publish"
                : "Publish"}
          </button>
        )}
        <button
          className="figma-btn figma-cancel-btn"
          onClick={() => setShowCancelModal(true)}
          disabled={
            actionLoading ||
            sale?.status === "cancelled" ||
            sale?.status?.toLowerCase() !== "draft" ||
            autoSaveMutation.isPending
          }
          title={
            autoSaveMutation.isPending ? "Waiting for changes to save" : ""
          }
        >
          <X size={16} />
          {actionLoading ? "Cancelling..." : "Cancel Sale"}
        </button>
        <button
          className={`figma-btn ${isPartsURL
              ? "figma-draft-save-btn-parts"
              : isAccessoriesURL
                ? "figma-draft-save-btn-acc"
                : "figma-draft-save-btn"
            } ${hasModifications ? "has-changes" : ""}`}
          title={
            hasModifications ? "You have unsaved changes" : "Save current draft"
          }
          onClick={handleSaveChanges}
          disabled={
            saveLoading ||
            autoSaveMutation.isPending ||
            sale?.status?.toLowerCase() !== "draft"
          }
        >
          <SaveIcon
            fillColor={
              isPartsURL
                ? "var(--primary-color-parts)"
                : isAccessoriesURL
                  ? "var(--primary-color-acc)"
                  : "var(--primary-color-mobile)"
            }
          />
          {saveLoading
            ? "Saving..."
            : autoSaveMutation.isPending
              ? "Auto-saving..."
              : hasModifications
                ? "Save Changes"
                : "Save Draft"}
        </button>
      </div>

      {/* Modals */}
      <PublishSaleModal
        isOpen={showPublishModal}
        onClose={() => setShowPublishModal(false)}
        onConfirm={handlePublishConfirm}
        saleData={saleData}
        loading={actionLoading}
      />

      {/* Delete Modal */}
      {showDeleteProductModal && deletingProduct && (
        <>
          <div className="modal-overlay">
            <div className="delete-modal">
              <div className="modal-icon">
                <img src={DeleteIcon} alt="Delete" />
              </div>
              <h3 className="modal-title">Delete Product</h3>
              <p className="modal-message">
                Are you sure you want to delete product{" "}
                <b>{deletingProduct.name}</b>?
                <br />
                This action cannot be undone.
              </p>
              <div className="modal-actions">
                <button
                  className="modal-cancel-btn"
                  onClick={() => setShowDeleteProductModal(false)}
                >
                  Cancel
                </button>
                <button
                  className="modal-delete-btn"
                  onClick={() => handleDeleteProduct(deletingProduct.id)}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Last Product Error Modal */}
      {showLastProductErrorModal && (
        <>
          <div className="modal-overlay">
            <div className="delete-modal">
              <div className="modal-icon">
                <img src={DeleteIcon} alt="Error" />
              </div>
              <h3 className="modal-title">Cannot Delete Product</h3>
              <p className="modal-message">
                You cannot delete the last product. At least one product must
                remain in the sale.
              </p>
              <div className="modal-actions">
                <button
                  className="modal-cancel-btn"
                  onClick={() => setShowLastProductErrorModal(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      <CancelSaleModal
        isOpen={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        onConfirm={handleCancelConfirm}
        loading={actionLoading}
      />

      <AddProductModal
        isOpen={showAddProductModal}
        onClose={() => setShowAddProductModal(false)}
        onAddProduct={handleAddProduct}
      />
    </div>
  );
};

export default ViewSales;
